/**
 * Timestamp formatting utilities — DEC-004 / §44 compliance.
 *
 * Phase 9 / Plan 09-04 — ALL timestamp rendering in web-dispatch MUST go through
 * this module. NEVER call `.toLocaleTimeString()` bare — it may display UTC.
 * NEVER call `.toUTCString()`.
 *
 * Phase 9 / Plan 09-05 — extended with staleness formatters and isStale().
 * The REAL timezone comes from GET /v1/users/me/profile via useUserTimezone().
 * DEFAULT_TZ is ONLY a pre-resolution fallback; once the profile query resolves,
 * the dispatcher's real timezone is used for all "as of HH:MM" labels.
 *
 * DEC-004 / §44 — all timestamps local-by-context. NEVER toLocaleTimeString()
 * bare; NEVER UTC. timeZone comes from the dispatcher's profile
 * (GET /v1/users/me/profile via useUserTimezone); DEFAULT_TZ is only the
 * pre-resolution fallback.
 *
 * Spec: §44 (local-by-context timestamp rendering); DEC-004; RESEARCH Pitfall 6.
 */

/** The application fallback timezone when the user profile timezone is not yet loaded. */
export const DEFAULT_TIMEZONE = 'America/Chicago';

/**
 * Alias for DEFAULT_TIMEZONE — used by staleness formatters and the
 * use-user-timezone hook (DEC-004 pre-resolution fallback only).
 */
export const DEFAULT_TZ = DEFAULT_TIMEZONE;

/**
 * Staleness threshold in minutes. A driver pin whose positionAt is older
 * than this value gets the dashed-warning border + "as of HH:MM" label.
 * Documented per UI-SPEC §4: "planner discretion — use 10 minutes".
 */
export const STALENESS_THRESHOLD_MIN = 10;

/**
 * Format an ISO timestamp string for display to the dispatcher.
 *
 * Uses `Intl.DateTimeFormat` with an explicit `timeZone` — never bare
 * `toLocaleTimeString()` or `toUTCString()` (§44 / DEC-004 hard requirement).
 *
 * @param isoString  ISO 8601 timestamp string (e.g. "2026-06-13T14:30:00Z").
 * @param timeZone   IANA timezone identifier. Defaults to 'America/Chicago'.
 *                   Plan 09-05 replaces the default with the user profile timezone.
 * @returns Formatted date+time string in the given timezone (e.g. "Jun 13, 2:30 PM").
 */
export function formatTimestamp(isoString: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(date);
  } catch {
    return '—';
  }
}

/**
 * Format an ISO timestamp for display as a short time-only string (e.g. "2:32 PM").
 * Used in the notification bell and staleness labels.
 *
 * @param isoString  ISO 8601 timestamp string.
 * @param timeZone   IANA timezone identifier. Defaults to 'America/Chicago'.
 */
export function formatTime(isoString: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(date);
  } catch {
    return '—';
  }
}

/**
 * Format an ISO timestamp as the map-pin staleness label: "as of 2:32 PM".
 *
 * DEC-004 (NON-NEGOTIABLE) — uses Intl.DateTimeFormat with an explicit timeZone.
 * Pass the dispatcher's profile timezone from useUserTimezone(); only falls back
 * to 'America/Chicago' (DEFAULT_TZ) before the profile query resolves.
 *
 * @param isoString  ISO 8601 position timestamp (positionAt from DriverPosition).
 * @param timeZone   IANA timezone identifier. Defaults to DEFAULT_TZ (Chicago).
 * @returns "as of 2:32 PM" — UI-SPEC §4 staleness label format.
 */
export function formatStaleness(isoString: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return 'as of —';
    const time = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(date);
    return `as of ${time}`;
  } catch {
    return 'as of —';
  }
}

/**
 * Format an ISO timestamp as the map-pin hover tooltip text.
 *
 * DEC-004 (NON-NEGOTIABLE) — uses Intl.DateTimeFormat with an explicit timeZone.
 *
 * @param isoString  ISO 8601 position timestamp.
 * @param timeZone   IANA timezone identifier. Defaults to DEFAULT_TZ (Chicago).
 * @returns "Last known position — as of Fri, May 23 2:32 PM" (UI-SPEC §4 tooltip format).
 */
export function formatStalenessTooltip(
  isoString: string,
  timeZone: string = DEFAULT_TIMEZONE,
): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return 'Last known position — as of —';
    const formatted = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(date);
    return `Last known position — as of ${formatted}`;
  } catch {
    return 'Last known position — as of —';
  }
}

/**
 * Format an ISO timestamp with a full date, e.g. "May 23, 2026 2:32 PM".
 *
 * Used by the InReview surface (queue rows, detail panel fields, audit timeline)
 * where a bare time is ambiguous because tickets sit in review across days.
 *
 * DEC-066 — 12-hour AM/PM, like every other formatter here. This previously
 * passed `hour12: false` and rendered "14:32", which made InReview the only
 * 24-hour surface in dispatch (86bc83kj7). `hour12` is now set explicitly rather
 * than relying on the en-US default, so the clock format is stated in the code
 * instead of being an implicit property of the locale.
 *
 * DEC-004 compliant — Intl.DateTimeFormat with an explicit timeZone, never UTC.
 *
 * @param isoString  ISO 8601 timestamp string.
 * @param timeZone   IANA timezone identifier. Defaults to DEFAULT_TZ (Chicago).
 */
export function formatCreated(isoString: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '—';
    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone,
    }).format(date);
    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    }).format(date);
    return `${datePart} ${timePart}`;
  } catch {
    return '—';
  }
}

/**
 * Returns true when the driver's last-known position is older than the staleness
 * threshold (default: STALENESS_THRESHOLD_MIN = 10 minutes).
 *
 * Used by DriverMap to apply the dashed-warning border + "as of HH:MM" label on
 * offline/stale driver pins (UI-SPEC §4 + D-03 honesty contract).
 *
 * @param isoString     ISO 8601 position timestamp (positionAt from DriverPosition).
 * @param thresholdMin  Staleness threshold in minutes. Defaults to STALENESS_THRESHOLD_MIN.
 */
export function isStale(
  isoString: string,
  thresholdMin: number = STALENESS_THRESHOLD_MIN,
): boolean {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return true; // treat invalid timestamp as stale
    const ageMs = Date.now() - date.getTime();
    return ageMs > thresholdMin * 60 * 1000;
  } catch {
    return true;
  }
}
